import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truck, Gauge, AlertOctagon, WifiOff } from 'lucide-react'
import { useSession } from '../auth/useSession'
import { EmptyState } from '../cc/EmptyState'
import { KpiCard } from '../cc/KpiCard'
import { KpiStrip } from '../cc/KpiStrip'
import { EYEBROW } from '../cc/tokens'
import { SidePanel } from '../cc/SidePanel'
import { SearchBox } from '../cc/SearchBox'
import { FilterChips, type FilterChipOption } from '../cc/FilterChips'
import { SortBar, type SortOption } from '../cc/SortBar'
import { VehicleCard, type VehicleStatus } from '../cc/VehicleCard'
import { GeofenceCard } from '../cc/GeofenceCard'
import { ago, clock, lastSeen, vehicleState, type VehicleState } from '../lib/format'
import {
  isAlertVisible, isSustainedAlertStillOpen, isSustainedConditionKind,
  SUSTAINED_ALERT_LIVENESS_MS, useSustainedAlertDeadSet,
} from '../lib/alertVisibility'
import { rewriteStopMessage } from '../lib/stopPlace'
import { locate } from '../lib/pointInPolygon'
import { loadLabel } from '../map/layers/vehicles'
import { defaultSurfaceFor, fleetPills, pathFor, surfacePills, type FleetId } from '../cc/fleetNav'
import { TopBar } from '../cc/TopBar'
import { useFleet } from '../state/store'
import type { Actionable, Alert, GeofenceProps } from '../lib/types'
import { WonderCementMap } from './WonderCementMap'
import { wondercementGeo } from './wondercementGeo'

const FLEET_ID = 'transvolt-wondercement'

/** Wonder Cement's known 6-truck roster (bridge/sites.yaml, db/fleet/vehicles.csv) --
 *  used to attribute an ACTIONABLE to Wonder Cement by plate, NOT by membership in the
 *  live `vehicles` snapshot. `/api/state`'s vehicles map is replaced wholesale on every
 *  bootstrap and pruned by `vehicles_removed` (state/store.ts), so a plate that has
 *  stopped reporting -- the single most likely state for the truck an open breakdown
 *  actionable is ABOUT, and the live state of 2 of these 6 plates right now -- would
 *  otherwise silently drop out of a live-roster filter and take its own actionable with
 *  it, right when it matters most. */
const WC_PLATES = new Set([
  'RJ09GF2231', 'RJ09GF2232', 'RJ09GF2233', 'RJ09GF2234', 'RJ09GF2235', 'RJ09GF2237',
])

/** `source_folder` is on every live geofence feature but not on `GeofenceProps` -- same
 *  intersection `wondercementGeo.ts` uses, for the same reason (SPEC.md Interfaces). */
type LiveGeofenceProps = GeofenceProps & { source_folder?: string | null }

function sessionDisplayName(session: ReturnType<typeof useSession>): string | undefined {
  return session.status === 'signed-in' ? session.session.name || session.session.email : undefined
}

/** `vehicleState()`'s four states don't line up 1:1 with `VehicleCard`'s five-state
 *  union (that union also carries 'loading'/'alert', TIMS/cargo concepts Wonder Cement
 *  has none of today) -- SPEC.md edge case: charging -> loading (the dot's only other
 *  blue), stale -> offline (its only other grey/neutral). */
const CARD_STATUS: Record<VehicleState, VehicleStatus> = {
  moving: 'moving', idle: 'idle', charging: 'loading', stale: 'offline',
}

const FLEET_FILTERS: FilterChipOption[] = [
  { id: 'all', label: 'All' },
  { id: 'moving', label: 'Moving' },
  { id: 'idle', label: 'Idle' },
  { id: 'charging', label: 'Charging' },
]

const SORTS: SortOption[] = [
  { id: 'breakdown', label: 'Breakdown' },
  { id: 'speed', label: 'Speed' },
  { id: 'lastping', label: 'Latest' },
]

const SEV_TONE: Record<string, 'danger' | 'warn' | 'info'> = {
  CRITICAL: 'danger', WARN: 'warn', INFO: 'info',
}

const PANEL_TABS = [
  { id: 'fleet', label: 'FLEET' },
  { id: 'alerts', label: 'ALERTS' },
  { id: 'actionables', label: 'ACTIONABLES' },
  { id: 'geofences', label: 'GEOFENCES' },
  { id: 'offline', label: 'OFFLINE' },
] as const

/** The three alert kinds the ACTIONABLES KPI tile rolls up -- overspeed and idling
 *  (geofence.py) and TAT breach (tat.py) are all real, already-emitted kinds; see each
 *  rule module's own EMITS declaration. Kept as a list, not inlined per-count, so the
 *  tile's total and its "N overspeed · N idling · N TAT breach" breakdown can't drift. */
const ACTIONABLE_ALERT_KINDS = ['OVERSPEED', 'EXCESSIVE_IDLING', 'TAT_BREACH'] as const

function LiveView(): JSX.Element {
  const rawVehicles = useFleet((s) => s.vehicles)
  const rawAlerts = useFleet((s) => s.alerts)
  const rawActionables = useFleet((s) => s.actionables)
  const rawRoutes = useFleet((s) => s.routes)
  const rawGeofences = useFleet((s) => s.geofences)
  const fenceIndex = useFleet((s) => s.fenceIndex)
  // Unfiltered, same as CommandCentre.tsx's own `meta` read -- it is looked up by a
  // SPECIFIC vehicle_id everywhere below (isLaden/loadLabel), never enumerated, so a
  // Wonder Cement plate cannot collide with an Ultratech one the way a full-roster
  // iteration would need protecting against.
  const meta = useFleet((s) => s.meta)
  // Live route-direction match -- what actually drives laden/empty for Wonder Cement
  // (quixapp's FLEET_ROUTE_IDS already carries a 'wondercement' entry, matching the
  // published wondercement-laden/wondercement-empty corridor); meta.load_state alone
  // cannot, since Wonder Cement's gates are single bidirectional points, not one-way
  // carriageway pairs like Ultratech's sets_load_state fences.
  const progress = useFleet((s) => s.progress)
  const [now, setNow] = useState(() => Date.now())
  const sustainedAlertDeadSet = useSustainedAlertDeadSet()
  // What the FLEET tab's filter/sort is computed against -- separate from `now` (which
  // ticks every 2s for live elapsed-time/SOC display) on purpose. `vehicles` comes from
  // the store's single shared, cross-fleet Map, whose identity changes on every SSE
  // vehicle event from ANY fleet (Ultratech's ~40 trucks included) -- resorting on every
  // one of those ticks would reorder rows out from under an operator's cursor mid-click.
  // Same "re-sorts every 8s, not on every SSE event" convention CommandCentre.tsx's own
  // sort memo documents.
  const [sortTick, setSortTick] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 2000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const id = setInterval(() => setSortTick(Date.now()), 8000)
    return () => clearInterval(id)
  }, [])

  const vehicles = useMemo(
    () => [...rawVehicles.values()].filter((v) => v.fleet === FLEET_ID),
    [rawVehicles],
  )
  const alerts = useMemo(
    () => rawAlerts.filter((a) => a.fleet === FLEET_ID),
    [rawAlerts],
  )
  const alertsToday = useMemo(() => {
    const since = now - 24 * 60 * 60 * 1000
    return alerts.filter((a) => a.ts >= since).length
  }, [alerts, now])

  // Live-tile TTL (.claude/work/SPEC.md): the ALERTS tab's tile list is a live surface
  // an operator watches in real time, so a tile must stop showing 5 minutes after its
  // own `ts` -- unlike alertsToday/actionableBreakdown above, which are 24h KPI
  // aggregates and must keep counting it. Reuses the existing 2s `now` tick, no new timer.
  // Sustained-condition kinds (EXCESSIVE_IDLING/UNSCHEDULED_STOP) are exempted from the
  // ts TTL entirely -- see isSustainedAlertStillOpen's own comment for the staleness
  // gate/excluded-state gate/resurrection latch/max-age backstop this composes.
  // vehicleState()'s 'charging' bucket (same classification stateCounts/offlineVehicles
  // below already trust) is reused for isExcludedState, but isLive is computed from a
  // DEDICATED, much shorter bound (SUSTAINED_ALERT_LIVENESS_MS, 5 minutes) rather than
  // vehicleState's own 'stale' bucket (STALE_AFTER_MS, 45 minutes, calibrated for
  // map-pin desaturation -- round 4 MED). "At a hub" is not checked -- same documented,
  // accepted gap as Ultratech's identical call site.
  const stateByVehicle = useMemo(
    () => new Map(vehicles.map((v) => [v.vehicle_id, vehicleState(v, now)])),
    [vehicles, now],
  )
  const lastSeenByVehicle = useMemo(
    () => new Map(vehicles.map((v) => [v.vehicle_id, lastSeen(v)])),
    [vehicles],
  )
  const visibleAlerts = useMemo(
    () => alerts.filter((a) => {
      if (!a.vehicle_id || !isSustainedConditionKind(a.kind)) return isAlertVisible(a.ts, now)
      const state = stateByVehicle.get(a.vehicle_id) ?? 'stale'
      const lastSeenTs = lastSeenByVehicle.get(a.vehicle_id)
      return isSustainedAlertStillOpen({
        alertId: a.alert_id,
        ts: a.ts,
        now,
        isLive: lastSeenTs != null && now - lastSeenTs <= SUSTAINED_ALERT_LIVENESS_MS,
        isMoving: state === 'moving',
        isExcludedState: state === 'charging',
      }, sustainedAlertDeadSet)
    }),
    [alerts, now, stateByVehicle, lastSeenByVehicle, sustainedAlertDeadSet],
  )

  // WC_PLATES, not vehicleIds from the live roster -- see WC_PLATES' own comment.
  const actionables = useMemo(
    () => rawActionables.filter((a) => a.vehicle_id != null && WC_PLATES.has(a.vehicle_id)),
    [rawActionables],
  )
  const actionableVehicleIds = useMemo(
    () => new Set(actionables.map((a) => a.vehicle_id).filter((id): id is string => id != null)),
    [actionables],
  )

  // Wonder Cement's own slice of the global /api/geo payload -- the one place this split
  // happens (SPEC.md Interfaces), shared by this panel's GEOFENCES tab and the map below.
  const geo = useMemo(() => wondercementGeo(rawRoutes, rawGeofences), [rawRoutes, rawGeofences])

  const wcFenceIds = useMemo(
    () => new Set(geo.geofences.features.map((f) => f.properties.geofence_id)),
    [geo],
  )
  const wcFenceIndex = useMemo(
    () => fenceIndex.filter((f) => wcFenceIds.has(f.id)),
    [fenceIndex, wcFenceIds],
  )
  const fenceOccupancy = useMemo(() => {
    const occ = new Map<string, number>()
    for (const v of vehicles) {
      if (v.lat == null || v.lon == null || !wcFenceIndex.length) continue
      const hit = locate(v.lon, v.lat, wcFenceIndex)
      if (hit) occ.set(hit.id, (occ.get(hit.id) ?? 0) + 1)
    }
    return occ
  }, [vehicles, wcFenceIndex])

  const vehicleById = useMemo(
    () => new Map(vehicles.map((v) => [v.vehicle_id, v])),
    [vehicles],
  )

  // Rolling 24h, same window alertsToday already uses -- the sibling "Alerts (24h)" tile
  // and this one must never disagree on what "today" means.
  const actionableBreakdown = useMemo(() => {
    const since = now - 24 * 60 * 60 * 1000
    const recent = alerts.filter((a) => a.ts >= since)
    const counts = {} as Record<(typeof ACTIONABLE_ALERT_KINDS)[number], number>
    for (const kind of ACTIONABLE_ALERT_KINDS) counts[kind] = recent.filter((a) => a.kind === kind).length
    return counts
  }, [alerts, now])
  const actionablesTotal = ACTIONABLE_ALERT_KINDS.reduce((sum, k) => sum + actionableBreakdown[k], 0)

  // sortTick, not now: the FLEET tab's own filter-chip counts below read vehicleState at
  // the same cadence (see sortTick's own comment) -- the KPI strip and the panel
  // disagreeing on "how many are moving right now" for up to 8s would read as a bug.
  const stateCounts = useMemo(() => {
    const by = { moving: 0, idle: 0, charging: 0, stale: 0 }
    for (const v of vehicles) by[vehicleState(v, sortTick)] += 1
    return by
  }, [vehicles, sortTick])

  // OFFLINE tab: the same 'stale' bucket the FLEET tab's status dot already renders as
  // "Offline" (CARD_STATUS above), surfaced as its own list instead of requiring a filter
  // click -- longest-silent first, so the plate that has been dark the longest sorts to
  // the top of an operator's attention.
  const offlineVehicles = useMemo(
    () => vehicles
      .filter((v) => vehicleState(v, sortTick) === 'stale')
      .sort((a, b) => lastSeen(a) - lastSeen(b)),
    [vehicles, sortTick],
  )

  const [panelTab, setPanelTab] = useState<string>('fleet')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState('breakdown')
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null)
  const [selectedFenceId, setSelectedFenceId] = useState<string | null>(null)

  const fleetRows = useMemo(() => {
    let rows = vehicles
    const q = query.trim().toLowerCase()
    if (q) rows = rows.filter((v) => v.vehicle_id.toLowerCase().includes(q))
    if (filter !== 'all') rows = rows.filter((v) => vehicleState(v, sortTick) === filter)

    const sorted = [...rows]
    if (sort === 'speed') {
      sorted.sort((a, b) => (b.speed_kph ?? 0) - (a.speed_kph ?? 0))
    } else if (sort === 'lastping') {
      sorted.sort((a, b) => lastSeen(b) - lastSeen(a))
    } else {
      // "Breakdown": a vehicle with an open actionable (ACTIONABLES tab) outranks
      // everything else, same priority CommandCentre.tsx's own default sort gives a
      // `down` vehicle -- the dedicated Breakdowns-style queue already reflects this; the
      // Fleet list reads the same way, not a different "worst first" that disagrees.
      const rank = { stale: 0, idle: 1, charging: 2, moving: 3 } as const
      sorted.sort((a, b) => {
        const aDown = actionableVehicleIds.has(a.vehicle_id) ? 1 : 0
        const bDown = actionableVehicleIds.has(b.vehicle_id) ? 1 : 0
        return (bDown - aDown) || (rank[vehicleState(a, sortTick)] - rank[vehicleState(b, sortTick)])
      })
    }
    return sorted
  }, [vehicles, query, filter, sort, sortTick, actionableVehicleIds])

  return (
    <>
      <KpiStrip>
        <KpiCard label="Vehicles reporting" value={vehicles.length} unit="/ 6" tone="info" dot />
        <KpiCard
          label="Actionables"
          value={actionablesTotal}
          unit="24h"
          dot
          tone={actionablesTotal ? 'warn' : 'ok'}
          sub={`${actionableBreakdown.OVERSPEED} overspeed · ${actionableBreakdown.EXCESSIVE_IDLING} idling · ${actionableBreakdown.TAT_BREACH} TAT breach`}
        />
        <KpiCard label="Moving / idle / charging" value={`${stateCounts.moving} / ${stateCounts.idle} / ${stateCounts.charging}`} />
        <KpiCard
          label="Alerts (24h)"
          value={alertsToday}
          tone={alertsToday > 0 ? 'warn' : 'ok'}
          dot
        />
      </KpiStrip>

      <div className="mt-3 flex min-h-0 flex-1 gap-3">
        <div className="w-[320px] shrink-0">
          <SidePanel
            tabs={PANEL_TABS.map((t) => ({
              ...t,
              count: t.id === 'fleet' ? vehicles.length
                // visibleAlerts, not alerts: this badge labels the ALERTS tab's own tile
                // list below (panelTab === 'alerts'), which is TTL-filtered -- counting
                // the un-filtered 24h `alerts` here would show e.g. "3" on the tab while
                // the panel itself renders "No alerts" (ship Phase 4 adversarial review,
                // round 1). `alertsToday`'s KPI card above is the intentional 24h count.
                : t.id === 'alerts' ? visibleAlerts.length
                : t.id === 'actionables' ? actionables.length
                : t.id === 'offline' ? offlineVehicles.length
                : geo.geofences.features.length,
            }))}
            active={panelTab}
            onTab={setPanelTab}
          >
            {panelTab === 'fleet' && (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
                <SearchBox value={query} onChange={setQuery}
                           placeholder="Plate…"
                           counter={`${fleetRows.length}/${vehicles.length}`} />
                <FilterChips
                  options={FLEET_FILTERS.map((f) => ({
                    ...f,
                    count: f.id === 'all' ? vehicles.length
                      : vehicles.filter((v) => vehicleState(v, sortTick) === f.id).length,
                  }))}
                  active={filter}
                  onPick={setFilter}
                />
                <SortBar options={SORTS} active={sort} onPick={setSort} />
                <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                  {fleetRows.length === 0 ? (
                    <EmptyState icon={<Truck size={26} />} title="No vehicles match"
                                body="Widen the filter, or clear the search." />
                  ) : fleetRows.map((v) => {
                    const state = vehicleState(v, sortTick)
                    return (
                      <VehicleCard
                        key={v.vehicle_id}
                        plate={v.vehicle_id}
                        status={CARD_STATUS[state]}
                        statusLabel={state}
                        place={v.lat != null && v.lon != null ? 'on corridor' : 'no fix'}
                        load={loadLabel(meta.get(v.vehicle_id), undefined, progress.get(v.vehicle_id))}
                        soc={v.soc_pct}
                        speed={v.speed_kph}
                        elapsed={ago(lastSeen(v), now)}
                        selected={selectedVehicleId === v.vehicle_id}
                        onClick={() => setSelectedVehicleId(v.vehicle_id)}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {panelTab === 'alerts' && (
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {visibleAlerts.length === 0 ? (
                  <EmptyState icon={<AlertOctagon size={26} />} title="No alerts"
                              body="Nothing on the live feed for Wonder Cement yet." />
                ) : visibleAlerts.map((a: Alert) => (
                  <VehicleCard
                    key={a.alert_id}
                    plate={a.vehicle_id ?? (a.cell ? `cell ${a.cell.slice(0, 8)}` : 'fleet')}
                    status={a.severity === 'CRITICAL' ? 'alert' : 'idle'}
                    statusLabel={a.kind.replace(/_/g, ' ').toLowerCase()}
                    place={a.driver_name || '—'}
                    elapsed={`${clock(a.ts)} · ${ago(a.ts, now)}`}
                    exception={{
                      // Live position wins when there is one (the vehicle may have moved
                      // since the alert fired); the alert's own captured fix is the
                      // fallback -- same preference CommandCentre.tsx's own ALERTS tab
                      // gives vehicles.get(...)?.lat over a.lat.
                      text: rewriteStopMessage(
                        a.message,
                        vehicleById.get(a.vehicle_id ?? '')?.lat ?? a.lat,
                        vehicleById.get(a.vehicle_id ?? '')?.lon ?? a.lon,
                        geo.geofences,
                      ),
                      tone: SEV_TONE[a.severity] ?? 'warn',
                    }}
                    onClick={() => a.vehicle_id && setSelectedVehicleId(a.vehicle_id)}
                  />
                ))}
              </div>
            )}

            {panelTab === 'actionables' && (
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {actionables.length === 0 ? (
                  <EmptyState icon={<AlertOctagon size={26} />} title="Nothing waiting"
                              body="Open breakdowns and unresolved criticals appear here." />
                ) : actionables.map((item: Actionable) => {
                  const v = item.vehicle_id ? vehicleById.get(item.vehicle_id) : undefined
                  return (
                    <VehicleCard
                      key={item.id}
                      plate={item.vehicle_id ?? (item.kind === 'alert' ? 'fleet' : '—')}
                      status="alert"
                      statusLabel={item.kind === 'breakdown'
                        ? `${item.severity} · down`
                        : item.title.replace(/_/g, ' ').toLowerCase()}
                      place={rewriteStopMessage(item.message || item.title, v?.lat ?? null, v?.lon ?? null, geo.geofences)}
                      elapsed={ago(item.ts, now)}
                      exception={{
                        icon: item.kind === 'breakdown' ? 'power' : 'alert',
                        text: item.kind === 'breakdown'
                          ? (item.title || 'Breakdown')
                          : rewriteStopMessage(item.message || item.title, v?.lat ?? null, v?.lon ?? null, geo.geofences),
                        tone: item.severity === 'immobilised' || item.severity === 'CRITICAL' ? 'danger' : 'warn',
                      }}
                      onClick={() => item.vehicle_id && setSelectedVehicleId(item.vehicle_id)}
                    />
                  )
                })}
              </div>
            )}

            {panelTab === 'geofences' && (
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {geo.geofences.features.length === 0 ? (
                  <EmptyState icon={<Gauge size={26} />} title="No geofences loaded"
                              body="Wonder Cement's geofences haven't published yet." />
                ) : geo.geofences.features.map((f) => {
                  const p = f.properties as LiveGeofenceProps
                  return (
                    <GeofenceCard
                      key={p.geofence_id}
                      name={p.name}
                      type={p.type}
                      hubCode={p.hub_code}
                      inside={fenceOccupancy.get(p.geofence_id) ?? 0}
                      speedLimitKph={p.speed_limit_kph}
                      tatMinutes={p.tat_minutes}
                      areaKm2={p.area_km2}
                      selected={selectedFenceId === p.geofence_id}
                      onClick={() => setSelectedFenceId(p.geofence_id)}
                    />
                  )
                })}
              </div>
            )}

            {panelTab === 'offline' && (
              <div className="min-h-0 flex-1 space-y-1.5 overflow-auto pr-0.5">
                {offlineVehicles.length === 0 ? (
                  <EmptyState icon={<WifiOff size={26} />} title="Nothing offline"
                              body="Every truck has reported recently." />
                ) : offlineVehicles.map((v) => (
                  <VehicleCard
                    key={v.vehicle_id}
                    plate={v.vehicle_id}
                    status="offline"
                    statusLabel="offline"
                    place={v.lat != null && v.lon != null ? 'last fix on corridor' : 'no fix'}
                    elapsed={ago(lastSeen(v), now)}
                    selected={selectedVehicleId === v.vehicle_id}
                    onClick={() => setSelectedVehicleId(v.vehicle_id)}
                  />
                ))}
              </div>
            )}
          </SidePanel>
        </div>

        <div className="min-h-0 min-w-0 flex-1">
          <WonderCementMap
            vehicles={vehicles} geo={geo} meta={meta} progress={progress}
            focusVehicleId={selectedVehicleId} focusFenceId={selectedFenceId}
          />
        </div>
      </div>
    </>
  )
}

/** Wonder Cement's own console -- live map + Command-Centre-style left panel (SPEC.md).
 *  Reads the SAME shared useFleet() store CommandCentre.tsx uses (populated once, app-wide,
 *  by useFleetData() in AppShell.tsx) filtered to fleet === 'transvolt-wondercement' --
 *  no dedicated backend endpoint or polling loop needed, because Wonder Cement's telemetry
 *  flows through the SAME generic pipeline Ultratech's does (see
 *  bridge/wondercement_decode_fanout.py and bridge/sites.yaml's own comment on why).
 *  Deliberately NOT CommandCentre.tsx itself, and does not import from it -- Trips (no
 *  TIMS integration, no fleet-scoped /api/ops/summary), the breakdown-reporting workflow,
 *  trip replay and the detail-drawer drill-down are explicit Non-goals (SPEC.md). */
export function WonderCementShell(): JSX.Element {
  const navigate = useNavigate()
  const session = useSession()
  const userLabel = sessionDisplayName(session)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#E9EEF5]">
      <div className="shrink-0 px-3 pt-3">
        <TopBar
          clock={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
          live
          user={userLabel}
          fleets={fleetPills(true)}
          activeFleet="wondercement"
          onFleet={(id) => navigate(pathFor(id as FleetId, defaultSurfaceFor(id as FleetId)))}
          nav={surfacePills('wondercement')}
          activeNav="live"
          onNav={() => {}}
          onSettings={() => navigate('/settings')}
        />
      </div>

      <div className="shrink-0 flex items-end justify-between gap-3 px-3 pt-3">
        <div>
          <p className={EYEBROW}>Rajasthan · Pipalkhunt ↔ Pratapgarh ↔ Nimbahera · BEM</p>
          <h1 className="font-display text-[22px] font-semibold tracking-tight text-[#0F172A]">
            Wonder Cement
          </h1>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <LiveView />
      </div>
    </div>
  )
}
